import React, { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Trophy, Upload, Loader2, CheckCircle2, XCircle, Search,
  Settings2, Send, Users, DollarSign, FileSpreadsheet, Trash2,
  CreditCard, Plus, Eye, SendHorizonal, ScanLine, Sparkles, ImageIcon,
} from "lucide-react";
import { Label } from "@/components/ui/label";
import PinDialog from "@/components/PinDialog";

function catShortPreview(name) {
  return name.toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/\u0111/g, "d").replace(/\u0110/g, "D")
    .replace(/\s+/g, "").replace("hangmuc:", "").replace("hangmuc", "");
}

export default function TournamentCRM() {
  const [tournaments, setTournaments] = useState([]);
  const [selectedTournId, setSelectedTournId] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [newTournName, setNewTournName] = useState("");
  const [newTournDesc, setNewTournDesc] = useState("");
  const [creatingTourn, setCreatingTourn] = useState(false);

  const [participants, setParticipants] = useState([]);
  const [categories, setCategories] = useState([]);
  const [configs, setConfigs] = useState([]);
  const [stats, setStats] = useState({ total: 0, paid: 0, unpaid: 0, zns_sent: 0, zns_failed: 0, zns_not_sent: 0 });
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [filterCat, setFilterCat] = useState("");
  const [filterPay, setFilterPay] = useState("");
  const [filterZns, setFilterZns] = useState("");
  const [searchQ, setSearchQ] = useState("");

  const [configOpen, setConfigOpen] = useState(false);
  const [editCfg, setEditCfg] = useState({ category: "", transfer_amount: "" });

  const [znsTemplateId, setZnsTemplateId] = useState("");
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [pinOpen, setPinOpen] = useState(false);
  const [pinAction, setPinAction] = useState(null);
  const [pinDesc, setPinDesc] = useState("");

  // Lazy load pagination for participants
  const crmPageRef = React.useRef(1);
  const crmLoadingRef = React.useRef(false);
  const crmHasMoreRef = React.useRef(false);
  const [crmTotal, setCrmTotal] = useState(0);
  const crmEndRef = React.useRef(null);

  // Load ZNS template ID from tournament data
  useEffect(() => {
    if (!selectedTournId) return;
    const t = tournaments.find(x => x.id === selectedTournId);
    setZnsTemplateId(t?.zns_template_id || "");
  }, [selectedTournId, tournaments]);

  // Selection
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [sendingRow, setSendingRow] = useState("");

  // Bulk send progress
  const [bulkSending, setBulkSending] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ i: 0, total: 0, sent: 0, failed: 0 });
  const [bulkLog, setBulkLog] = useState([]);
  const [bulkDone, setBulkDone] = useState(false);
  const [progressOpen, setProgressOpen] = useState(false);

  // Preview
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);

  // AI Receipt
  const [receiptOpen, setReceiptOpen] = useState(false);
  const [receiptAnalyzing, setReceiptAnalyzing] = useState(false);
  const [receiptResult, setReceiptResult] = useState(null);
  const [receiptConfirming, setReceiptConfirming] = useState(false);

  // Highlight VĐV vừa được cập nhật payment_status (nhấp nháy)
  const [highlightedPaymentIds, setHighlightedPaymentIds] = useState(new Set());



  const loadTournaments = useCallback(async () => {
    try {
      const { data } = await api.get("/tournaments");
      setTournaments(data.tournaments || []);
      if (!selectedTournId && data.tournaments?.length > 0) {
        setSelectedTournId(data.tournaments[0].id);
      }
    } catch (e) { console.error(e); }
  }, [selectedTournId]);

  useEffect(() => { loadTournaments(); }, []);

  const loadParticipants = useCallback(async (append = false) => {
    if (!selectedTournId) { setParticipants([]); setCategories([]); setStats({ total: 0, paid: 0, unpaid: 0, zns_sent: 0, zns_failed: 0, zns_not_sent: 0 }); setCrmTotal(0); return; }
    if (crmLoadingRef.current) return;
    crmLoadingRef.current = true;
    if (!append) { setLoading(true); crmPageRef.current = 1; }
    try {
      const pg = append ? crmPageRef.current + 1 : 1;
      let url = `/tournament/participants?tournament_id=${selectedTournId}&page=${pg}&limit=30`;
      if (filterCat) url += `&category=${encodeURIComponent(filterCat)}`;
      if (filterPay) url += `&payment_status=${filterPay}`;
      if (filterZns) url += `&zns_status=${filterZns}`;
      if (searchQ) url += `&search=${encodeURIComponent(searchQ)}`;
      const { data } = await api.get(url);
      const newItems = data.participants || [];
      if (append) {
        setParticipants(prev => {
          const combined = [...prev, ...newItems];
          crmHasMoreRef.current = combined.length < (data.filtered_total || 0);
          return combined;
        });
      } else {
        setParticipants(newItems);
        crmHasMoreRef.current = newItems.length < (data.filtered_total || 0);
      }
      crmPageRef.current = pg;
      setCrmTotal(data.filtered_total || 0);
      setCategories(data.categories || []);
      setStats({
        total: data.total, paid: data.paid, unpaid: data.unpaid,
        zns_sent: data.zns_sent || 0, zns_failed: data.zns_failed || 0, zns_not_sent: data.zns_not_sent || 0,
      });
    } catch (e) { console.error(e); }
    finally { crmLoadingRef.current = false; setLoading(false); }
  }, [selectedTournId, filterCat, filterPay, filterZns, searchQ]);

  const loadConfigs = useCallback(async () => {
    if (!selectedTournId) { setConfigs([]); return; }
    try {
      const { data } = await api.get(`/tournament/config?tournament_id=${selectedTournId}`);
      setConfigs(data.configs || []);
    } catch (e) { console.error(e); }
  }, [selectedTournId]);

  useEffect(() => { loadParticipants(); }, [loadParticipants]);
  useEffect(() => { loadConfigs(); }, [loadConfigs]);
  useEffect(() => { setFilterCat(""); setFilterPay(""); setFilterZns(""); setSearchQ(""); setSelectedIds(new Set()); crmPageRef.current = 1; crmHasMoreRef.current = false; }, [selectedTournId]);

  // Infinite scroll observer for participants
  useEffect(() => {
    const el = crmEndRef.current;
    if (!el) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !crmLoadingRef.current && crmHasMoreRef.current) {
        loadParticipants(true);
      }
    }, { threshold: 0.1 });
    observer.observe(el);
    return () => observer.disconnect();
  }, [loadParticipants, crmTotal]);

  const handleCreateTourn = async () => {
    if (!newTournName.trim()) { toast.error("Vui lòng nhập tên giải đấu"); return; }
    setCreatingTourn(true);
    try {
      const { data } = await api.post("/tournaments", { name: newTournName.trim(), description: newTournDesc.trim() });
      toast.success(`Đã tạo giải "${data.tournament.name}"`);
      setCreateOpen(false); setNewTournName(""); setNewTournDesc("");
      await loadTournaments();
      setSelectedTournId(data.tournament.id);
    } catch (err) { toast.error(err.response?.data?.detail || "Lỗi tạo giải"); }
    finally { setCreatingTourn(false); }
  };

  const handleDeleteTourn = () => {
    const t = tournaments.find(x => x.id === selectedTournId);
    if (!t) return;
    setPinDesc(`Xóa giải "${t.name}" và toàn bộ VĐV?`);
    setPinAction(() => async () => {
      try {
        const { data } = await api.delete(`/tournaments/${selectedTournId}`);
        toast.success(data.message);
        setSelectedTournId(""); loadTournaments();
      } catch (err) { toast.error(err.response?.data?.detail || "Lỗi xóa"); }
    });
    setPinOpen(true);
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedTournId) return;
    const formData = new FormData();
    formData.append("file", file);
    formData.append("tournament_id", selectedTournId);
    setUploading(true);
    // Reset highlight khi bắt đầu upload mới
    setHighlightedPaymentIds(new Set());
    try {
      const { data } = await api.post("/tournament/import-excel", formData, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(data.message);
      // Lưu danh sách VĐV vừa được cập nhật payment_status để nhấp nháy
      if (data.updated_payment_ids?.length > 0) {
        setHighlightedPaymentIds(new Set(data.updated_payment_ids));
        toast.info(`${data.updated_payment_ids.length} VĐV được cập nhật trạng thái thanh toán`);
      }
      loadParticipants(); loadConfigs(); loadTournaments();
    } catch (err) { toast.error(err.response?.data?.detail || "Lỗi import"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const togglePayment = async (id, current) => {
    const newStatus = current === "paid" ? "unpaid" : "paid";
    try {
      await api.put(`/tournament/participants/${id}/payment-status`, { payment_status: newStatus });
      setParticipants(prev => prev.map(p => p.id === id ? { ...p, payment_status: newStatus } : p));
      if (newStatus === "paid") setStats(s => ({ ...s, paid: s.paid + 1, unpaid: s.unpaid - 1 }));
      else setStats(s => ({ ...s, paid: s.paid - 1, unpaid: s.unpaid + 1 }));
    } catch { toast.error("Lỗi cập nhật"); }
  };

  const handleSaveConfig = async () => {
    try {
      await api.put("/tournament/config", { ...editCfg, tournament_id: selectedTournId });
      toast.success("Đã lưu cấu hình " + editCfg.category); setConfigOpen(false); loadConfigs();
    } catch { toast.error("Lỗi lưu"); }
  };

  const openConfig = (cat) => {
    const cfg = configs.find(c => c.category === cat) || { category: cat, transfer_amount: "" };
    setEditCfg({ category: cfg.category, transfer_amount: cfg.transfer_amount || "" });
    setConfigOpen(true);
  };

  // Bulk send all unpaid (filtered)
  const handleSendAll = async () => {
    setSending(true); setSendResult(null);
    try {
      const { data } = await api.post("/tournament/send-zns", {
        tournament_id: selectedTournId, category: filterCat, zns_template_id: znsTemplateId,
      });
      setSendResult(data); toast.success(data.message); loadParticipants();
    } catch (err) { toast.error(err.response?.data?.detail || "Lỗi gửi"); }
    finally { setSending(false); }
  };

  // Send to selected participants with progress polling
  const handleSendSelected = async () => {
    const ids = [...selectedIds];
    if (ids.length === 0) { toast.error("Chưa chọn VĐV"); return; }
    setBulkSending(true);
    setBulkProgress({ i: 0, total: ids.length, sent: 0, failed: 0 });
    setBulkLog([]);
    setBulkDone(false);
    setProgressOpen(true);

    // Send in batches of 1, updating progress after each
    let sent = 0, failed = 0;

    for (let idx = 0; idx < ids.length; idx++) {
      const pid = ids[idx];
      try {
        const { data } = await api.post("/tournament/send-zns-selected", {
          tournament_id: selectedTournId, participant_ids: [pid], zns_template_id: znsTemplateId,
        });
        if (data.sent > 0) {
          sent++;
          const r = data.results?.[0] || {};
          setBulkLog(prev => [...prev, { i: idx + 1, total: ids.length, name: r.name || "", phone: r.phone || "", status: "sent", error: "" }]);
        } else {
          failed++;
          const r = data.results?.[0] || {};
          setBulkLog(prev => [...prev, { i: idx + 1, total: ids.length, name: r.name || "", phone: r.phone || "", status: "failed", error: r.error || data.message || "" }]);
        }
      } catch (err) {
        failed++;
        const errMsg = err.response?.data?.detail || err.message || "Lỗi";
        setBulkLog(prev => [...prev, { i: idx + 1, total: ids.length, name: "", phone: "", status: "failed", error: errMsg }]);
      }
      setBulkProgress({ i: idx + 1, total: ids.length, sent, failed });
    }

    setBulkDone(true);
    setBulkSending(false);
    loadParticipants();
    setSelectedIds(new Set());
  };

  // Send to single participant
  const handleSendOne = async (pid) => {
    setSendingRow(pid);
    try {
      const { data } = await api.post("/tournament/send-zns-selected", {
        tournament_id: selectedTournId, participant_ids: [pid], zns_template_id: znsTemplateId,
      });
      toast.success(data.message);
      // Update local zns_status
      const newStatus = data.failed > 0 ? "failed" : "sent";
      setParticipants(prev => prev.map(p => p.id === pid ? { ...p, zns_status: newStatus } : p));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Lỗi gửi");
      setParticipants(prev => prev.map(p => p.id === pid ? { ...p, zns_status: "failed" } : p));
    }
    finally { setSendingRow(""); }
  };

  // Preview
  const handlePreview = async (ids) => {
    if (!ids || ids.length === 0) { toast.error("Chưa chọn VĐV"); return; }
    setPreviewLoading(true);
    try {
      const { data } = await api.post("/tournament/preview-zns", { tournament_id: selectedTournId, participant_ids: ids });
      setPreviewData(data.previews || []); setPreviewOpen(true);
    } catch (err) { toast.error("Lỗi xem trước"); }
    finally { setPreviewLoading(false); }
  };

  // AI Receipt Analysis - multi-file
  const handleReceiptUpload = async (e) => {
    const fileList = e.target.files;
    if (!fileList?.length || !selectedTournId) return;
    setReceiptAnalyzing(true);
    setReceiptResult(null);
    setReceiptOpen(true);
    try {
      const formData = new FormData();
      formData.append("tournament_id", selectedTournId);
      for (let i = 0; i < fileList.length; i++) {
        formData.append(`file${i}`, fileList[i]);
      }
      const { data } = await api.post("/tournament/analyze-receipts", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      setReceiptResult(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Lỗi phân tích ảnh");
      setReceiptOpen(false);
    } finally {
      setReceiptAnalyzing(false);
      e.target.value = "";
    }
  };

  const confirmReceiptIds = async (ids, info) => {
    if (ids.length === 0) return;
    try {
      const { data } = await api.post("/tournament/confirm-receipt", {
        participant_ids: ids, receipt_info: info || {},
      });
      toast.success(data.message);
      // Update local result to show confirmed
      setReceiptResult(prev => {
        if (!prev) return prev;
        const confirmed = new Set(ids);
        return {
          ...prev,
          results: prev.results.map(r => ({
            ...r,
            matched_participants: r.matched_participants?.map(mp =>
              confirmed.has(mp.id) ? { ...mp, payment_status: "paid", just_confirmed: true } : mp
            ),
          })),
        };
      });
      loadParticipants();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Lỗi xác nhận");
    }
  };

  const handleConfirmReceipt = async (fileIndex) => {
    setReceiptConfirming(true);
    try {
      if (fileIndex !== undefined) {
        // Single image confirm
        const r = receiptResult.results[fileIndex];
        const ids = (r?.matched_participants || []).map(mp => mp.id);
        await confirmReceiptIds(ids, r?.analysis);
      } else {
        // Confirm all
        const allIds = [];
        for (const r of receiptResult.results) {
          for (const mp of (r.matched_participants || [])) {
            if (!allIds.includes(mp.id) && mp.payment_status !== "paid") allIds.push(mp.id);
          }
        }
        await confirmReceiptIds(allIds, receiptResult.results[0]?.analysis);
      }
    } finally { setReceiptConfirming(false); }
  };



  // Selection helpers
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSelectAll = () => {
    if (selectedIds.size === participants.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(participants.map(p => p.id)));
  };

  const catStats = {};
  participants.forEach(p => {
    if (!catStats[p.category]) catStats[p.category] = { total: 0, paid: 0 };
    catStats[p.category].total++;
    if (p.payment_status === "paid") catStats[p.category].paid++;
  });

  const selectedTourn = tournaments.find(t => t.id === selectedTournId);
  const unpaidForSend = participants.filter(p => {
    return p.payment_status === "unpaid" && p.phone && (!filterCat || p.category === filterCat);
  });

  return (
    <div className="space-y-4" data-testid="tab-content-tournament">
      {/* Tournament Selector */}
      <div className="bg-[#1e293b] border border-[#334155] rounded-lg p-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Trophy className="w-5 h-5 text-amber-400" />
            <h2 className="text-base font-bold text-[#F8FAFC]">Quản lý giải đấu</h2>
          </div>
          <Button onClick={() => setCreateOpen(true)} className="bg-lime-500 hover:bg-lime-400 text-[#0f172a] font-semibold gap-1.5 text-xs" data-testid="create-tournament-btn">
            <Plus className="w-3.5 h-3.5" /> Tạo giải mới
          </Button>
        </div>
        {tournaments.length > 0 ? (
          <div className="flex items-center gap-3 mt-3 flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <Select value={selectedTournId} onValueChange={setSelectedTournId}>
                <SelectTrigger className="bg-[#0f172a] border-[#334155] text-slate-200 h-9" data-testid="tournament-select"><SelectValue placeholder="Chọn giải đấu..." /></SelectTrigger>
                <SelectContent className="bg-[#1e293b] border-[#334155]">
                  {tournaments.map(t => (<SelectItem key={t.id} value={t.id} className="text-slate-300">{t.name} ({t.participant_count} VĐV)</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            {selectedTourn && (
              <>
                <div className="relative">
                  <input type="file" accept=".xlsx,.xls" className="hidden" id="import-excel-file" onChange={handleUpload} />
                  <Button type="button" onClick={() => document.getElementById("import-excel-file")?.click()} disabled={uploading} className="bg-emerald-600 hover:bg-emerald-500 text-white gap-1.5 text-xs font-semibold" data-testid="import-excel-btn">
                    {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileSpreadsheet className="w-3.5 h-3.5" />}
                    {uploading ? "Đang import..." : "Import Excel"}
                  </Button>
                </div>
                <div className="relative">
                  <input type="file" accept="image/*" multiple className="hidden" id="receipt-upload" onChange={handleReceiptUpload} />
                  <Button type="button" onClick={() => document.getElementById("receipt-upload")?.click()} disabled={receiptAnalyzing}
                    className="bg-violet-600 hover:bg-violet-500 text-white gap-1.5 text-xs font-semibold" data-testid="receipt-upload-btn">
                    {receiptAnalyzing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ScanLine className="w-3.5 h-3.5" />}
                    {receiptAnalyzing ? "Đang phân tích..." : "AI Kiểm tra CK"}
                  </Button>
                </div>
                <Button variant="outline" size="sm" onClick={handleDeleteTourn} className="border-red-500/30 text-red-400 hover:bg-red-500/10 text-xs gap-1" data-testid="delete-tournament-btn">
                  <Trash2 className="w-3 h-3" /> Xóa giải
                </Button>
              </>
            )}
          </div>
        ) : (
          <p className="text-slate-500 text-xs mt-3">Chưa có giải đấu nào. Bấm "Tạo giải mới" để bắt đầu.</p>
        )}
      </div>

      {selectedTournId && stats.total > 0 && (
        <>
          {/* Stats + Categories */}
          <div className="bg-[#1e293b] border border-[#334155] rounded-lg p-4 space-y-3">
            <div className="flex flex-wrap gap-2">
              {[
                { key: "", label: "Tổng", value: stats.total, clr: "#38bdf8", filterType: "pay", icon: <Users className="w-3.5 h-3.5" /> },
                { key: "paid", label: "Đã TT", value: stats.paid, clr: "#84cc16", filterType: "pay", icon: <CheckCircle2 className="w-3.5 h-3.5" /> },
                { key: "unpaid", label: "Chưa TT", value: stats.unpaid, clr: "#f59e0b", filterType: "pay", icon: <CreditCard className="w-3.5 h-3.5" /> },
                { key: "sent", label: "Đã gửi ZNS", value: stats.zns_sent, clr: "#34d399", filterType: "zns", icon: <Send className="w-3.5 h-3.5" /> },
                { key: "not_sent", label: "Chưa gửi", value: stats.zns_not_sent, clr: "#94a3b8", filterType: "zns", icon: <Send className="w-3.5 h-3.5" /> },
                { key: "failed", label: "Gửi lỗi", value: stats.zns_failed, clr: "#f87171", filterType: "zns", icon: <XCircle className="w-3.5 h-3.5" /> },
              ].map(s => {
                const isActive = s.filterType === "pay"
                  ? (s.key === "" ? !filterPay : filterPay === s.key)
                  : filterZns === s.key;
                return (
                  <button key={s.key + s.filterType} onClick={() => {
                    if (s.filterType === "pay") setFilterPay(prev => prev === s.key ? "" : s.key);
                    else setFilterZns(prev => prev === s.key ? "" : s.key);
                  }}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs border transition-all cursor-pointer"
                    style={{
                      color: isActive ? s.clr : "#94a3b8",
                      borderColor: isActive ? s.clr + "50" : "#334155",
                      backgroundColor: isActive ? s.clr + "15" : "#0f172a",
                    }}
                    data-testid={`stat-${s.filterType}-${s.key || "all"}`}>
                    {s.icon}
                    {s.label}: <span className="font-bold" style={{ color: s.clr }}>{s.value}</span>
                  </button>
                );
              })}
            </div>
            {categories.length > 0 && (
              <div className="space-y-1.5">
                <Label className="text-slate-500 text-[10px] uppercase tracking-wider font-medium">Nội dung thi đấu</Label>
                <div className="flex flex-wrap gap-2">
                  {categories.map(cat => {
                    const cs = catStats[cat] || { total: 0, paid: 0 };
                    const cfg = configs.find(c => c.category === cat);
                    return (
                      <div key={cat} className="flex items-center gap-1">
                        <button onClick={() => setFilterCat(prev => prev === cat ? "" : cat)}
                          className={`px-3 py-1.5 rounded-lg text-xs border transition-colors ${filterCat === cat ? "bg-amber-500/15 text-amber-400 border-amber-500/30" : "text-slate-400 border-[#334155] hover:text-slate-200"}`}
                          data-testid={`cat-filter-${cat}`}>
                          {cat} <span className="text-[10px] ml-1 opacity-70">({cs.paid}/{cs.total})</span>
                        </button>
                        <button onClick={() => openConfig(cat)} className={`p-1 rounded transition-colors ${cfg?.transfer_amount ? "text-lime-400 hover:text-lime-300" : "text-slate-600 hover:text-amber-400"}`} title="Cấu hình lệ phí" data-testid={`cat-config-${cat}`}>
                          <Settings2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Table */}
          <div className="bg-[#1e293b] border border-[#334155] rounded-lg overflow-hidden">
            {/* Action bar: search + filters + selection actions */}
            <div className="flex items-center gap-2 p-3 border-b border-[#334155] flex-wrap">
              <div className="relative flex-1 min-w-[180px]">
                <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
                <Input value={searchQ} onChange={(e) => setSearchQ(e.target.value)} placeholder="Tìm theo tên, SĐT, nickname..." className="bg-[#0f172a] border-[#334155] text-slate-200 pl-8 h-8 text-xs" data-testid="tournament-search" />
              </div>
              <Select value={filterCat || "all"} onValueChange={(v) => setFilterCat(v === "all" ? "" : v)}>
                <SelectTrigger className="w-[180px] bg-[#0f172a] border-[#334155] text-slate-300 h-8 text-xs" data-testid="tournament-filter-cat"><SelectValue placeholder="Tất cả trình" /></SelectTrigger>
                <SelectContent className="bg-[#1e293b] border-[#334155]">
                  <SelectItem value="all" className="text-slate-300">Tất cả trình</SelectItem>
                  {categories.map(c => (<SelectItem key={c} value={c} className="text-slate-300">{c}</SelectItem>))}
                </SelectContent>
              </Select>
              {selectedIds.size > 0 && (
                <div className="flex items-center gap-1.5 ml-auto">
                  <Badge className="bg-sky-500/15 text-sky-400 border-sky-500/20 text-[10px] px-2 py-0.5 border">
                    {selectedIds.size} VĐV
                  </Badge>
                  <Button size="sm" variant="outline" onClick={() => handlePreview([...selectedIds])} disabled={previewLoading}
                    className="border-sky-500/30 text-sky-400 hover:bg-sky-500/10 text-[11px] gap-1 h-7" data-testid="preview-selected-btn">
                    <Eye className="w-3 h-3" /> Xem trước
                  </Button>
                  <Button size="sm" onClick={handleSendSelected} disabled={sending || bulkSending}
                    className="bg-amber-500 text-[#0f172a] font-semibold hover:bg-amber-400 text-[11px] gap-1 h-7" data-testid="send-selected-btn">
                    {bulkSending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                    Gửi {selectedIds.size} VĐV
                  </Button>
                </div>
              )}
              {sendResult && (
                <span className={`text-xs ${sendResult.sent > 0 ? "text-lime-400" : "text-amber-400"}`}>{sendResult.message}</span>
              )}
            </div>

            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-[#334155] hover:bg-transparent">
                    <TableHead className="text-slate-400 w-8 px-2">
                      <Checkbox checked={participants.length > 0 && selectedIds.size === participants.length}
                        onCheckedChange={toggleSelectAll} className="border-slate-500" data-testid="select-all-cb" />
                    </TableHead>
                    <TableHead className="text-slate-400 text-xs w-14">STT</TableHead>
                    <TableHead className="text-slate-400 text-xs">Tên VĐV</TableHead>
                    <TableHead className="text-slate-400 text-xs">SĐT</TableHead>
                    <TableHead className="text-slate-400 text-xs">Trình</TableHead>
                    <TableHead className="text-slate-400 text-xs">Đồng đội</TableHead>
                    <TableHead className="text-slate-400 text-xs">ND chuyển khoản</TableHead>
                    <TableHead className="text-slate-400 text-xs">Mã HĐ</TableHead>
                    <TableHead className="text-slate-400 text-xs text-center">Thanh toán</TableHead>
                    <TableHead className="text-slate-400 text-xs text-center w-20">Gửi ZNS</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow className="border-[#334155]"><TableCell colSpan={10} className="text-center py-8"><Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-500" /></TableCell></TableRow>
                  ) : participants.length === 0 ? (
                    <TableRow className="border-[#334155]"><TableCell colSpan={10} className="text-center py-8 text-slate-500 text-sm">Không tìm thấy VĐV</TableCell></TableRow>
                  ) : participants.map(p => (
                    <TableRow key={p.id} className={`border-[#334155] hover:bg-[#334155]/20 ${selectedIds.has(p.id) ? "bg-sky-500/5" : ""} ${p.replaced ? "bg-orange-500/5 border-l-2 border-l-orange-400" : ""} ${p.ai_confirmed ? "bg-violet-500/5" : ""} ${highlightedPaymentIds.has(p.id) ? "payment-highlight" : ""}`} data-testid={`tp-row-${p.id}`}>
                      <TableCell className="px-2">
                        <Checkbox checked={selectedIds.has(p.id)} onCheckedChange={() => toggleSelect(p.id)} className="border-slate-500" data-testid={`tp-cb-${p.id}`} />
                      </TableCell>
                      <TableCell className="text-slate-400 text-xs font-mono font-medium">{p.display_stt || p.stt || "—"}</TableCell>
                      <TableCell>
                        <div>
                          <span className="text-slate-200 text-sm font-medium">{p.name}</span>
                          {p.nickname && <span className="text-slate-500 text-xs ml-1.5">({p.nickname})</span>}
                        </div>
                        {p.replaced && (
                          <span className="text-[10px] text-orange-400">Thay: {p.replaced_from}</span>
                        )}
                        {!p.replaced && p.city && <span className="text-slate-600 text-[10px]">{p.city}</span>}
                      </TableCell>
                      <TableCell className="text-slate-400 text-xs font-mono">{p.phone || "—"}</TableCell>
                      <TableCell>
                        <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/20 text-[10px] px-1.5 py-0 border">{p.category}</Badge>
                      </TableCell>
                      <TableCell className="text-slate-400 text-xs">{p.partner_name || "—"}</TableCell>
                      <TableCell>
                        <code className="text-[10px] text-sky-400 bg-[#0f172a] px-1.5 py-0.5 rounded border border-[#334155]/50 font-mono">{p.bank_transfer_note}</code>
                      </TableCell>
                      <TableCell className="text-slate-500 text-[10px] font-mono">{p.contract_number || "—"}</TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          <button onClick={() => togglePayment(p.id, p.payment_status)}
                            className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium border transition-all cursor-pointer ${p.payment_status === "paid" ? "bg-lime-500/15 text-lime-400 border-lime-500/30 hover:bg-lime-500/25" : "bg-red-500/10 text-red-400 border-red-500/20 hover:bg-red-500/20"}`}
                            data-testid={`tp-pay-${p.id}`}>
                            {p.payment_status === "paid" ? <><CheckCircle2 className="w-3 h-3" /> Đã TT</> : <><XCircle className="w-3 h-3" /> Chưa TT</>}
                          </button>
                          {p.ai_confirmed && (
                            <span className="text-violet-400" title="AI xác nhận thanh toán"><Sparkles className="w-3.5 h-3.5" /></span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          <button onClick={() => handlePreview([p.id])} title="Xem trước"
                            className="p-1 rounded text-slate-500 hover:text-sky-400 transition-colors" data-testid={`tp-preview-${p.id}`}>
                            <Eye className="w-3.5 h-3.5" />
                          </button>
                          <button onClick={() => handleSendOne(p.id)} disabled={sendingRow === p.id || !p.phone} title="Gửi ZNS"
                            className={`p-1 rounded transition-colors disabled:opacity-30 ${
                              p.zns_status === "sent" ? "text-lime-400 hover:text-lime-300" :
                              p.zns_status === "failed" ? "text-red-400 hover:text-red-300" :
                              "text-slate-500 hover:text-amber-400"
                            }`} data-testid={`tp-send-${p.id}`}>
                            {sendingRow === p.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> :
                             p.zns_status === "sent" ? <CheckCircle2 className="w-3.5 h-3.5" /> :
                             p.zns_status === "failed" ? <XCircle className="w-3.5 h-3.5" /> :
                             <SendHorizonal className="w-3.5 h-3.5" />}
                          </button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <div className="text-[10px] text-slate-600 px-3 py-2 border-t border-[#334155]">
              Hiển thị {participants.length}/{crmTotal} VĐV{filterCat ? ` (${filterCat})` : ""}{selectedIds.size > 0 ? ` · Đã chọn ${selectedIds.size}` : ""}
            </div>
            <div ref={crmEndRef} className="flex justify-center p-2">
              {crmLoadingRef.current ? (
                <Loader2 className="w-4 h-4 animate-spin text-slate-500" />
              ) : participants.length > 0 && participants.length < crmTotal ? (
                <span className="text-[10px] text-slate-600">Kéo xuống để tải thêm</span>
              ) : participants.length > 0 ? (
                <span className="text-[10px] text-slate-600">Tất cả {crmTotal} VĐV</span>
              ) : null}
            </div>
          </div>
        </>
      )}

      {/* Empty state */}
      {selectedTournId && stats.total === 0 && !loading && (
        <div className="bg-[#1e293b] border border-[#334155] rounded-lg p-8 text-center space-y-3">
          <FileSpreadsheet className="w-10 h-10 text-slate-600 mx-auto" />
          <p className="text-slate-400 text-sm">Giải "{selectedTourn?.name}" chưa có dữ liệu VĐV</p>
          <p className="text-slate-600 text-xs">Upload file Excel (.xlsx) để import danh sách VĐV.</p>
          <div className="relative inline-block">
            <input type="file" accept=".xlsx,.xls" className="hidden" id="import-excel-empty" onChange={handleUpload} />
            <Button type="button" onClick={() => document.getElementById("import-excel-empty")?.click()} disabled={uploading} className="bg-emerald-600 hover:bg-emerald-500 text-white gap-1.5" data-testid="import-excel-empty-btn">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {uploading ? "Đang import..." : "Chọn file Excel"}
            </Button>
          </div>
        </div>
      )}

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="bg-[#1e293b] border-[#334155] sm:max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="preview-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#F8FAFC] font-heading flex items-center gap-2">
              <Eye className="w-4 h-4 text-sky-400" />
              Xem trước tin nhắn ZNS ({previewData.length} VĐV)
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 max-h-[500px] overflow-y-auto">
            {previewData.map((p, i) => (
              <div key={p.id} className="bg-[#0f172a] border border-[#334155] rounded-lg p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-slate-600 font-mono">#{i + 1}</span>
                    <span className="text-sm text-slate-200 font-medium">{p.name}</span>
                    <span className="text-xs text-slate-500">{p.phone}</span>
                    <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/20 text-[10px] px-1.5 py-0 border">{p.category}</Badge>
                  </div>
                  <Badge className={`text-[9px] px-1.5 py-0 border ${p.payment_status === "paid" ? "bg-lime-500/15 text-lime-400 border-lime-500/20" : "bg-amber-500/15 text-amber-400 border-amber-500/20"}`}>
                    {p.payment_status === "paid" ? "Đã TT" : "Chưa TT"}
                  </Badge>
                </div>
                <div className="bg-[#1e293b] rounded-md p-3 border border-[#334155]/50 space-y-1.5">
                  <p className="text-[10px] text-slate-500 font-medium uppercase tracking-wider">Nội dung ZNS gửi tới Zalo</p>
                  <p className="text-xs text-slate-300">
                    Karinox trân trọng thông báo đến Quý khách lệ phí trình <span className="text-amber-400 font-medium">{p.variables.trinh_vdv}</span> như sau:
                  </p>
                  <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
                    <span className="text-slate-500">Quý khách</span>
                    <span className="text-lime-400 font-medium">{p.variables.customer_name}</span>
                    <span className="text-slate-500">Mã hợp đồng</span>
                    <span className="text-sky-400 font-mono">{p.variables.contract_number}</span>
                    <span className="text-slate-500">Số tiền</span>
                    <span className="text-lime-400 font-bold">{p.variables.transfer_amount || "—"}</span>
                  </div>
                  <p className="text-[10px] text-slate-500 italic">Ghi chú: Quý khách vui lòng thanh toán để hợp lệ bốc thăm thi đấu.</p>
                </div>
                {p.bank_transfer_note && (
                  <p className="text-[10px] text-slate-600">
                    ND chuyển khoản: <code className="text-sky-400">{p.bank_transfer_note}</code>
                    {p.partner_name && <span className="ml-2">Đồng đội: {p.partner_name}</span>}
                  </p>
                )}
              </div>
            ))}
          </div>
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setPreviewOpen(false)} className="border-[#334155] text-slate-300 hover:bg-[#334155]">Đóng</Button>
            <Button onClick={() => { setPreviewOpen(false); handleSendSelected(); }} disabled={sending || selectedIds.size === 0}
              className="bg-amber-500 text-[#0f172a] font-semibold hover:bg-amber-400 gap-1">
              <Send className="w-3.5 h-3.5" /> Xác nhận gửi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Tournament Modal */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-[#1e293b] border-[#334155] sm:max-w-md" data-testid="create-tournament-modal">
          <DialogHeader>
            <DialogTitle className="text-[#F8FAFC] font-heading flex items-center gap-2">
              <Trophy className="w-4 h-4 text-amber-400" /> Tạo giải đấu mới
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-slate-400 text-xs">Tên giải đấu *</Label>
              <Input value={newTournName} onChange={(e) => setNewTournName(e.target.value)} placeholder="VD: Giải Pickleball Karinox tháng 5" className="bg-[#0f172a] border-[#334155] text-slate-50" data-testid="new-tourn-name" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-400 text-xs">Mô tả (tuỳ chọn)</Label>
              <Input value={newTournDesc} onChange={(e) => setNewTournDesc(e.target.value)} placeholder="VD: Giải chào mừng 30/4 - 01/5" className="bg-[#0f172a] border-[#334155] text-slate-50" data-testid="new-tourn-desc" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCreateOpen(false)} className="text-slate-400">Hủy</Button>
            <Button onClick={handleCreateTourn} disabled={creatingTourn} className="bg-lime-500 hover:bg-lime-400 text-[#0f172a] font-semibold" data-testid="confirm-create-tourn-btn">
              {creatingTourn ? "Đang tạo..." : "Tạo giải"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Receipt Analysis Dialog */}
      <Dialog open={receiptOpen} onOpenChange={(v) => { if (!receiptAnalyzing) setReceiptOpen(v); }}>
        <DialogContent className="bg-[#1e293b] border-[#334155] sm:max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="receipt-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#F8FAFC] flex items-center gap-2">
              {receiptAnalyzing ? <Loader2 className="w-4 h-4 text-violet-400 animate-spin" /> : <Sparkles className="w-4 h-4 text-violet-400" />}
              {receiptAnalyzing ? "Đang phân tích ảnh chuyển khoản..." : `Kết quả phân tích AI (${receiptResult?.total_files || 0} ảnh)`}
            </DialogTitle>
          </DialogHeader>

          {receiptAnalyzing && (
            <div className="py-12 text-center space-y-3">
              <ScanLine className="w-10 h-10 text-violet-400 mx-auto animate-pulse" />
              <p className="text-slate-400 text-sm">GPT-5.2 Vision đang đọc ảnh chuyển khoản...</p>
              <p className="text-slate-600 text-xs">Có thể mất 10-30 giây mỗi ảnh</p>
            </div>
          )}

          {!receiptAnalyzing && receiptResult?.results && (
            <div className="space-y-4 max-h-[500px] overflow-y-auto">
              {receiptResult.results.map((r, ri) => (
                <div key={ri} className="border border-[#334155] rounded-lg overflow-hidden">
                  {/* Image header */}
                  <div className="bg-[#0f172a] p-3 flex items-center gap-2 border-b border-[#334155]">
                    <ImageIcon className="w-4 h-4 text-slate-500" />
                    <span className="text-xs text-slate-300 font-medium">Ảnh {ri + 1}</span>
                    {r.error ? (
                      <Badge className="bg-red-500/10 text-red-400 border-red-500/20 text-[10px] border ml-auto">Lỗi</Badge>
                    ) : r.matched_participants?.length > 0 ? (
                      <Badge className="bg-violet-500/10 text-violet-400 border-violet-500/20 text-[10px] border ml-auto">
                        {r.matched_participants.length} VĐV khớp
                      </Badge>
                    ) : (
                      <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/20 text-[10px] border ml-auto">Không khớp</Badge>
                    )}
                  </div>

                  {r.error ? (
                    <div className="p-3 text-red-400 text-xs">{r.error}</div>
                  ) : (
                    <div className="p-3 space-y-3">
                      {/* Extracted info */}
                      <div className="grid grid-cols-2 gap-1.5 text-xs">
                        <div><span className="text-slate-500">Số tiền:</span> <span className="text-lime-400 font-bold">{r.analysis?.amount || "—"}</span></div>
                        <div><span className="text-slate-500">Người CK:</span> <span className="text-slate-200">{r.analysis?.sender_name || "—"}</span></div>
                        <div className="col-span-2"><span className="text-slate-500">ND chuyển khoản:</span> <span className="text-amber-400 font-medium">{r.analysis?.transfer_note || "—"}</span></div>
                        {r.analysis?.matched_note && r.analysis.matched_note !== r.analysis.transfer_note && (
                          <div className="col-span-2"><span className="text-slate-500">Khớp DB:</span> <span className="text-violet-400">{r.analysis.matched_note}</span></div>
                        )}
                      </div>

                      {/* Matched VĐV */}
                      {r.matched_participants?.length > 0 && (
                        <div className="space-y-1.5">
                          {r.matched_participants.map(mp => (
                            <div key={mp.id} className="flex items-center gap-2 bg-violet-500/5 border border-violet-500/20 rounded-lg px-3 py-2">
                              <Sparkles className="w-3.5 h-3.5 text-violet-400 shrink-0" />
                              <div className="flex-1 min-w-0">
                                <span className="text-slate-200 text-sm font-medium">{mp.name}</span>
                                {mp.partner_name && <span className="text-slate-500 text-xs ml-1">+ {mp.partner_name}</span>}
                                <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/20 text-[10px] px-1 py-0 border ml-2">{mp.category}</Badge>
                              </div>
                              <code className="text-[10px] text-sky-400">{mp.bank_transfer_note}</code>
                              <Badge className={`text-[9px] px-1 py-0 border shrink-0 ${mp.payment_status === "paid" ? "bg-lime-500/15 text-lime-400 border-lime-500/20" : "bg-amber-500/15 text-amber-400 border-amber-500/20"}`}>
                                {mp.payment_status === "paid" ? "Đã TT" : "Chưa TT"}
                              </Badge>
                            </div>
                          ))}
                        </div>
                      )}
                      {r.matched_participants?.length === 0 && !r.error && (
                        <p className="text-amber-400 text-xs text-center py-2">Không xác định được VĐV — nội dung CK không khớp</p>
                      )}
                      {/* Per-image confirm button */}
                      {r.matched_participants?.length > 0 && (
                        <div className="flex justify-end pt-1">
                          {r.matched_participants.every(mp => mp.just_confirmed || mp.payment_status === "paid") ? (
                            <span className="text-lime-400 text-xs flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Đã xác nhận</span>
                          ) : (
                            <Button size="sm" onClick={() => handleConfirmReceipt(ri)} disabled={receiptConfirming}
                              className="bg-violet-600 hover:bg-violet-500 text-white text-[11px] gap-1 h-7" data-testid={`confirm-receipt-${ri}`}>
                              <Sparkles className="w-3 h-3" /> Xác nhận ảnh {ri + 1}
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {!receiptAnalyzing && (
            <DialogFooter className="flex gap-2">
              <Button variant="outline" onClick={() => setReceiptOpen(false)} className="border-[#334155] text-slate-300 hover:bg-[#334155]">Đóng</Button>
              {receiptResult?.total_matched > 0 && receiptResult?.results?.length > 1 && (
                <Button onClick={() => handleConfirmReceipt()} disabled={receiptConfirming}
                  className="bg-violet-600 hover:bg-violet-500 text-white font-semibold gap-1.5" data-testid="confirm-all-receipt-btn">
                  {receiptConfirming ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  Xác nhận tất cả ({receiptResult.total_matched} VĐV)
                </Button>
              )}
              {receiptResult?.total_matched > 0 && receiptResult?.results?.length === 1 && (
                <Button onClick={() => handleConfirmReceipt(0)} disabled={receiptConfirming}
                  className="bg-violet-600 hover:bg-violet-500 text-white font-semibold gap-1.5" data-testid="confirm-receipt-btn">
                  {receiptConfirming ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  Xác nhận {receiptResult.total_matched} VĐV đã thanh toán
                </Button>
              )}
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {/* Config Modal */}
      <Dialog open={configOpen} onOpenChange={setConfigOpen}>
        <DialogContent className="bg-[#1e293b] border-[#334155] sm:max-w-md" data-testid="config-modal">
          <DialogHeader>
            <DialogTitle className="text-[#F8FAFC] font-heading flex items-center gap-2">
              <CreditCard className="w-4 h-4 text-amber-400" /> Cấu hình lệ phí — {editCfg.category}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-amber-400 text-xs font-medium">Số tiền lệ phí (VNĐ)</Label>
              <Input value={editCfg.transfer_amount} onChange={(e) => setEditCfg(c => ({ ...c, transfer_amount: e.target.value }))} placeholder="VD: 500.000" className="bg-[#0f172a] border-[#334155] text-slate-50" data-testid="cfg-transfer-amount" />
            </div>
            <div className="bg-[#0f172a] border border-[#334155] rounded p-3 space-y-1">
              <p className="text-[10px] text-slate-500 font-medium mb-1">BIẾN ZNS GỬI TỚI ZALO</p>
              <p className="text-xs text-slate-400"><span className="text-slate-500">customer_name</span> = <span className="text-lime-400">Tên VĐV</span></p>
              <p className="text-xs text-slate-400"><span className="text-slate-500">trinh_vdv</span> = <span className="text-amber-400">{editCfg.category || "Tên nội dung"}</span></p>
              <p className="text-xs text-slate-400"><span className="text-slate-500">transfer_amount</span> = <span className="text-lime-400">{editCfg.transfer_amount || "..."}</span></p>
              <p className="text-xs text-slate-400"><span className="text-slate-500">contract_number</span> = <span className="text-sky-400">#XXXXXDDMMYY (tự sinh)</span></p>
              <p className="text-xs text-slate-400 mt-1.5 pt-1.5 border-t border-[#334155]/50">
                <span className="text-slate-500">bank_transfer_note</span> = <span className="text-amber-400">TênVĐV1.TênVĐV2.{editCfg.category ? catShortPreview(editCfg.category) : "..."}</span>
                <span className="text-[10px] text-slate-600 ml-1">(ND chuyển khoản)</span>
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfigOpen(false)} className="text-slate-400">Hủy</Button>
            <Button onClick={handleSaveConfig} className="bg-lime-500 hover:bg-lime-400 text-[#0f172a] font-semibold" data-testid="cfg-save-btn">Lưu cấu hình</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Send Progress Dialog */}
      <Dialog open={progressOpen} onOpenChange={(v) => { if (bulkDone || !bulkSending) setProgressOpen(v); }}>
        <DialogContent className="bg-[#1e293b] border-[#334155] sm:max-w-lg max-h-[80vh]" data-testid="progress-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#F8FAFC] flex items-center gap-2">
              {bulkDone ? <CheckCircle2 className="w-4 h-4 text-lime-400" /> : <Loader2 className="w-4 h-4 text-amber-400 animate-spin" />}
              {bulkDone ? "Hoàn tất gửi ZNS" : `Đang gửi ZNS... ${bulkProgress.i}/${bulkProgress.total}`}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {/* Progress bar */}
            <div className="space-y-1.5">
              <div className="flex justify-between text-xs text-slate-400">
                <span>{bulkProgress.i}/{bulkProgress.total} VĐV</span>
                <span>{bulkProgress.total > 0 ? Math.round(bulkProgress.i / bulkProgress.total * 100) : 0}%</span>
              </div>
              <div className="w-full bg-[#0f172a] rounded-full h-2.5 border border-[#334155]">
                <div className="h-full rounded-full transition-all duration-300"
                  style={{
                    width: `${bulkProgress.total > 0 ? (bulkProgress.i / bulkProgress.total * 100) : 0}%`,
                    backgroundColor: bulkProgress.failed > 0 ? "#f59e0b" : "#84cc16",
                  }} />
              </div>
              <div className="flex gap-4 text-xs">
                <span className="text-lime-400">Thành công: {bulkProgress.sent}</span>
                {bulkProgress.failed > 0 && <span className="text-red-400">Thất bại: {bulkProgress.failed}</span>}
              </div>
            </div>
            {/* Log entries */}
            <div className="max-h-[300px] overflow-y-auto space-y-1 bg-[#0f172a] border border-[#334155] rounded-lg p-2">
              {bulkLog.length === 0 ? (
                <p className="text-slate-600 text-xs text-center py-4">Đang khởi tạo...</p>
              ) : [...bulkLog].reverse().map((lg, i) => (
                <div key={i} className={`flex items-center gap-2 px-2 py-1 rounded text-[11px] ${lg.status === "sent" ? "text-slate-300" : lg.status === "failed" ? "bg-red-500/10 text-red-400" : "text-slate-500"}`}>
                  {lg.status === "sent" ? <CheckCircle2 className="w-3 h-3 text-lime-400 shrink-0" /> :
                   lg.status === "failed" ? <XCircle className="w-3 h-3 text-red-400 shrink-0" /> :
                   <span className="w-3 h-3 shrink-0" />}
                  <span className="font-mono text-slate-600 w-7">{lg.i}</span>
                  <span className="truncate flex-1">{lg.name}</span>
                  <span className="text-slate-600 font-mono text-[10px]">{lg.phone}</span>
                  {lg.error && <span className="text-red-400 text-[10px] truncate max-w-[150px]">{lg.error}</span>}
                </div>
              ))}
            </div>
          </div>
          {bulkDone && (
            <DialogFooter>
              <Button onClick={() => setProgressOpen(false)} className="bg-lime-500 hover:bg-lime-400 text-[#0f172a] font-semibold">
                Đóng
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
      <PinDialog open={pinOpen} onOpenChange={setPinOpen} onConfirm={() => { if (pinAction) pinAction(); }} title="Xác nhận xóa" description={pinDesc} />
    </div>
  );
}
